import { motionValue, type MotionValue } from "framer-motion";
import { useLayoutEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router";

/** The three carousel strips, each with its own MotionValue and cleanup. */
export type DragTier = "page" | "content" | "image";

// ---------------------------------------------------------------------------
// Drag MotionValues – driven by useSwipeDrag, read by the carousel strips
// ---------------------------------------------------------------------------

/** Page-level drag offset (px). Drives StandardLayout's DragCarousel. */
export const pageDragX: MotionValue<number> = motionValue(0);

/** Content-level drag offset (px). Drives ContentDetailLayout's DragCarousel for content↔content. */
export const contentDragX: MotionValue<number> = motionValue(0);

/** Image-level drag offset (px). Drives MediaStrip. */
export const imageDragX: MotionValue<number> = motionValue(0);

// ---------------------------------------------------------------------------
// Drag type – which carousel is active during the current gesture
// ---------------------------------------------------------------------------

export type DragType = "page" | "content" | "image" | "rubberband" | null;

let currentDragType: DragType = null;
const dragTypeListeners = new Set<() => void>();

export function setDragType(type: DragType): void {
  currentDragType = type;
  dragTypeListeners.forEach((l) => l());
}

export function getDragType(): DragType {
  return currentDragType;
}

const subscribeDragType = (cb: () => void) => {
  dragTypeListeners.add(cb);
  return () => dragTypeListeners.delete(cb);
};

export function useDragType(): DragType {
  return useSyncExternalStore(subscribeDragType, getDragType, getDragType);
}

// ---------------------------------------------------------------------------
// Dragging flag – whether a drag gesture is in progress
// ---------------------------------------------------------------------------

let dragging = false;
const draggingListeners = new Set<() => void>();

export function setDragging(value: boolean): void {
  dragging = value;
  draggingListeners.forEach((l) => l());
}

export function isDragging(): boolean {
  return dragging;
}

const subscribeDragging = (cb: () => void) => {
  draggingListeners.add(cb);
  return () => draggingListeners.delete(cb);
};

export function useDraggingState(): boolean {
  return useSyncExternalStore(subscribeDragging, isDragging, isDragging);
}

// ---------------------------------------------------------------------------
// Skip next transition – tells AnimatedOutlet to skip animation after a
// drag-committed navigation (the carousel already showed the destination).
// ---------------------------------------------------------------------------

let skipNext = false;

export function setSkipNextTransition(): void {
  skipNext = true;
}

export function consumeSkipNextTransition(): boolean {
  const val = skipNext;
  skipNext = false;
  return val;
}

// ---------------------------------------------------------------------------
// Pending drag cleanup – defers MV reset + preview teardown until the router
// has committed the destination. Armed with the location key the gesture
// navigates away from; useDragCommitCleanup consumes it on the first render
// whose key differs, so a loader's loading render never resets the strip.
// ---------------------------------------------------------------------------

interface PendingDragCleanup {
  mv: MotionValue<number>;
  forType: DragTier;
  /** location.key at the commit; the reset waits for a different one. */
  fromKey: string;
}

let pendingCleanup: PendingDragCleanup | null = null;

export function setPendingDragCleanup(
  mv: MotionValue<number>,
  forType: DragTier,
  fromKey: string,
): void {
  pendingCleanup = { mv, forType, fromKey };
}

export function consumePendingDragCleanup(
  tier: DragTier,
  locationKey: string,
): PendingDragCleanup | null {
  if (!pendingCleanup || pendingCleanup.forType !== tier) return null;
  if (pendingCleanup.fromKey === locationKey) return null;
  const result = pendingCleanup;
  pendingCleanup = null;
  return result;
}

/**
 * Resets a strip after a drag commit, before the browser paints, but only on
 * a render whose location differs from the one the commit navigated away
 * from: the router renders the old location once more while loaders run.
 */
export function useDragCommitCleanup(tier: DragTier): void {
  const { key } = useLocation();
  useLayoutEffect(() => {
    const pending = consumePendingDragCleanup(tier, key);
    if (pending) {
      pending.mv.set(0);
      setDragging(false);
      setDragType(null);
    }
  });
}
